import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { components } from 'react-select'
import { Stack, Box } from '@mui/material'
import ContentFilters from './FiltersStyles'
import Select from 'components/global/CUK/AsyncSelect/Select'
import DateFilter from '../../../components/DateFilter'
import useFilters from '../store'
import useAggregation from './use-aggregation'
import { OptionMultiCount } from 'components/global/CUK/AsyncSelect/components/Option'
import { ClickAwayListener, Typography, useTheme } from '@mui/material'
import StateIcon from 'components/global/StateIcon'
import { FILTER_LAYOUT_TYPES } from './filterLayouts'
import { trackEvent } from 'analytics'

const DropdownIndicator = ({ ...props }) => {
  const theme = useTheme()
  return (
    <Box mr={1}>
      {props.selectProps.menuIsOpen ? (
        <StateIcon
          iconlabel={'angle-up'}
          iconweight={'regular'}
          iconcolor={theme.palette.primary.main}
        />
      ) : (
        <StateIcon iconweight={'regular'} iconlabel={'angle-down'} />
      )}
    </Box>
  )
}

const ValueContainer = ({ ...props }) => {
  const theme = useTheme()
  const selectedValuesCount = props.getValue().length
  const { menuIsOpen } = props.selectProps

  return (
    <components.ValueContainer {...props}>
      <Box>
        <Typography
          sx={{
            color: menuIsOpen ? '#3B82F6' : theme.palette.grey[500],
            fontSize: '12px',
            paddingLeft: theme.spacing(1.5),
            fontWeight: 500,
          }}
        >
          {selectedValuesCount === 0
            ? props.selectProps.placeholder
            : `${selectedValuesCount} sélectionnés`}
        </Typography>
      </Box>
    </components.ValueContainer>
  )
}

const Option = (props) => {
  return (
    <OptionMultiCount
      {...props}
      value={props.data}
      count={props.data.count}
      availability={props.data.value}
    />
  )
}

const StatusSelect = ({ counters, onChange, values = [], isStandard }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const choices = useMemo(() => {
    if (!isStandard) {
      return [{
        label: t('coupons.filters.available.standard'),
        value: 'available',
        count: counters.available,
        id: 1,
      }, {
        label: t('coupons.filters.used.standard'),
        value: 'used',
        count: counters.used,
        id: 2,
      }, {
        label: t('coupons.filters.expired.standard'),
        value: 'expired', 
        count: counters.expired,
        id: 3,
      }]
    }    
    return [
      {
        label: t('coupons.filters.available_soon.standard'),
        value: 'available_soon',
        count: counters.available_soon,
        id: 1,
      }, {
        label: t('coupons.filters.available.standard'),
        value: 'available',
        count: counters.available,
        id: 2,
      }, {
        label: t('coupons.filters.used.standard'),
        value: 'used',
        count: counters.used,
        id: 3,
      }, {
        label: t('coupons.filters.expired.standard'),
        value: 'expired',
        count: counters.expired,
        id: 4,
      }, {
        label: t('coupons.filters.disabled.standard'),
        value: 'disabled',
        count: counters.disabled,
        id: 5,
      }
    ]
  }, [counters, isStandard, t])
  
  return (
    <Select
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      options={choices}
      onChange={(values) => onChange(values.map(({ value }) => value))}
      onMenuOpen={() => trackEvent('tap_code_filter', {
        code_type: isStandard ? 'standard' : 'h3'
      })}
      mainField="label"
      placeholder={t('coupons.filters.status_placeholder')}
      components={{
        Option,
        Control: ({ children, ...props }) => {
          const { menuIsOpen, onMenuClose, onMenuOpen } = props.selectProps
          return (
            <components.Control {...props}>
              <ClickAwayListener
                mouseEvent='onMouseDown'
                onClickAway={() => {
                  onMenuClose()
                }}
              >
                <Box
                  onClick={() => {
                    if (menuIsOpen) {
                      onMenuClose()
                    } else {
                      onMenuOpen()
                    }
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexGrow: 1,
                    gap: 7,
                    cursor: 'pointer'
                  }}
                >
                  {children}
                </Box>
              </ClickAwayListener>
            </components.Control>
          )
        },
        MenuList: ({ children, ...props }) => {
          return (
            <components.MenuList {...props}>
              {children}
              <Box
                ml={1}
                mt={1}
                color={'grey.600'}
                fontSize={12}
                fontWeight={500}
                onClick={() => props.setValue([])}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.grey[800],
                  },
                }}
              >
                {t('actions.deselect_all')}
              </Box>
            </components.MenuList>
          )
        },
        DropdownIndicator,
        ValueContainer,
        Placeholder: (props) => (
          <Box>
            <components.Placeholder {...props} />
          </Box>
        ),
        IndicatorSeparator: null,
        ClearIndicator: null,
      }}
      value={choices.filter(
        (choice) => values.findIndex((value) => choice.value === value) > -1,
      )}
    />
  )
}

const dateFilterSettings = {
  creation: {
    placement: 'right-start',
    placeholderKey: 'coupons.filters.creation_date',
    filterKey: 'createdAt',
    analyticsName: 'code_creation_date'
  },
  usage: {
    placement: 'left-end',
    placeholderKey: 'coupons.filters.usage_date',
    filterKey: 'usageDate',
    analyticsName: 'code_usage_date'
  }
}

const Filters = ({
  coupons,
  isStandard,
  layout = FILTER_LAYOUT_TYPES.DESKTOP,
  dateTypes = ['creation', 'usage']
}) => {
  const [filters, setFilters] = useFilters()
  const { counters, loading, error } = useAggregation({ isStandard })
  const availableDateTypes = dateTypes.filter(type => isStandard || type === 'creation')

  const handleStatusChange = (values) => {
    const selectedFilter = values.find(value => !filters.status.includes(value))

    if (selectedFilter) {
      trackEvent('select_code_filter', {
        code_type: isStandard ? 'standard' : 'h3',
        filter_name: selectedFilter,
      })
    } else if (!values.length && filters.status.length) {
      trackEvent('reset_code_filter', {
        code_type: isStandard ? 'standard' : 'h3',
      })
    }

    setFilters({ ...filters, status: values })
  }

  const renderDateFilter = (type) => {
    const config = dateFilterSettings[type]
    if (!config) return null

    return (
      <DateFilter
        key={type}
        placement={config.placement}
        onReset={() => {
          setFilters((prev) => ({
            ...prev,
            [config.filterKey]: { start: null, end: null },
          }))
        }}
        startDate={filters[config.filterKey].start}
        endDate={filters[config.filterKey].end}
        onSubmit={(start, end) => {
          setFilters((prev) => ({
            ...prev,
            [config.filterKey]: { start, end },
          }))
        }}
        placeholderKey={config.placeholderKey}
        analyticsName={config.analyticsName}
        showPresets={true}
        blockFutureDates={type === 'creation'}
      />
    )
  }

  const renderStatusSelect = () => (
    <StatusSelect
      isStandard={isStandard}
      coupons={coupons}
      counters={counters}
      aggs={{ counters, loading, error }}
      onChange={handleStatusChange}
      values={filters.status}
    />
  )

  const renderDateFilters = () => (
    <>
      {availableDateTypes.map((type) => renderDateFilter(type))}
    </>
  )

  switch (layout) {
    case FILTER_LAYOUT_TYPES.MOBILE_COMPACT:
      return renderStatusSelect()

    case FILTER_LAYOUT_TYPES.MOBILE_EXTENDED:
      return (
        <Stack direction="row" gap={1} sx={{ width: '100%' }}>
          {availableDateTypes.map((type) => (
            <Box key={type} sx={{ flex: 1, minWidth: 0 }}>
              {renderDateFilter(type)}
            </Box>
          ))}
        </Stack>
      )

    case FILTER_LAYOUT_TYPES.TABLET:
      return (
        <Stack direction="row" gap={1} sx={{ width: '100%', flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: '200px', flex: 1 }}>
            {renderStatusSelect()}
          </Box>
          {availableDateTypes.map((type) => (
            <Box key={type} sx={{ minWidth: '150px' }}>
              {renderDateFilter(type)}
            </Box>
          ))}
        </Stack>
      )

    case FILTER_LAYOUT_TYPES.INLINE:
      return (
        <Stack direction="row" gap={1} sx={{ width: '100%', alignItems: 'center' }}>
          {renderStatusSelect()}
          {renderDateFilters()}
        </Stack>
      )

    case FILTER_LAYOUT_TYPES.STACKED:
      return (
        <Stack gap={1} sx={{ width: '100%' }}>
          {renderStatusSelect()}
          <Stack direction="row" gap={1}>
            {renderDateFilters()}
          </Stack>
        </Stack>
      )

    case FILTER_LAYOUT_TYPES.DESKTOP:
    default:
      return (
        <ContentFilters>
          {renderStatusSelect()}
          {renderDateFilters()}
        </ContentFilters>
      )
  }
}

export { FILTER_LAYOUT_TYPES }
export default Filters
